import { cn } from '../lib/utils';

interface StepperProps {
  /** 1-based index of the step currently in progress. */
  current: number;
  total: number;
  label: string;
}

/**
 * Dots and a count, for the first-run flow (epic #121).
 *
 * Deliberately minimal. ui-kit has no stepper, progress bar, checkbox or alert primitive, and this
 * needs exactly one of them in exactly one place — a general stepper component with orientation,
 * clickable steps and per-step state would be more surface than the product has a use for.
 */
export function Stepper({ current, total, label }: StepperProps) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2" role="presentation">
        {Array.from({ length: total }, (_, i) => {
          const index = i + 1;
          const done = index < current;
          const active = index === current;
          return (
            <div key={index} className="flex flex-1 items-center gap-2 last:flex-none">
              <span
                className={cn(
                  'h-2.5 w-2.5 shrink-0 rounded-full transition-colors',
                  done && 'bg-red-accent',
                  active && 'bg-red-accent ring-4 ring-red-accent/20',
                  !done && !active && 'bg-border',
                )}
              />
              {index < total && (
                <span className={cn('h-px flex-1 transition-colors', done ? 'bg-red-accent' : 'bg-border')} />
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs uppercase tracking-wide text-text-secondary">
        Step {current} of {total} · {label}
      </p>
    </div>
  );
}
